import { supabase } from '../lib/supabase.js';

export const metricsService = {
  // Get artist metrics overview
  async getArtistMetrics(artistId, timeRange = '30_days') {
    try {
      const dateFilter = this.getDateFilter(timeRange);
      
      // Get mobile app analytics
      const { data: mobileData, error: mobileError } = await supabase?.from('mobile_app_analytics')?.select('*')?.eq('artist_id', artistId)?.gte('metric_date', dateFilter)?.order('metric_date', { ascending: false });

      if (mobileError) throw mobileError;

      // Get traditional analytics
      const { data: userAnalytics, error: userError } = await supabase?.from('user_analytics')?.select('*')?.eq('user_id', artistId)?.gte('metric_date', dateFilter)?.order('metric_date', { ascending: false });

      if (userError) throw userError;

      // Get user activity data
      const { data: activityData, error: activityError } = await supabase?.from('user_activity')?.select('*')?.eq('user_id', artistId)?.gte('created_at', dateFilter)?.order('created_at', { ascending: false });

      if (activityError) throw activityError;

      // Process and combine data
      const processedData = this.processMetricsData(mobileData, userAnalytics, activityData, timeRange);
      
      return { data: processedData, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to fetch metrics' };
    }
  },

  // Get app download metrics with geographic data
  async getDownloadMetrics(artistId, timeRange = '30_days') {
    try {
      const dateFilter = this.getDateFilter(timeRange);
      
      const { data, error } = await supabase?.from('mobile_app_analytics')?.select('*')?.eq('artist_id', artistId)?.eq('metric_type', 'app_downloads')?.gte('metric_date', dateFilter)?.order('metric_date', { ascending: false });

      if (error) throw error;

      // Process geographic data for heat map
      const geoData = this.processGeographicData(data);
      
      return { data: geoData, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to fetch download metrics' };
    }
  },

  // Get page click analytics
  async getPageClickMetrics(artistId, timeRange = '30_days') {
    try {
      const dateFilter = this.getDateFilter(timeRange);
      
      const { data, error } = await supabase?.from('mobile_app_analytics')?.select('*')?.eq('artist_id', artistId)?.eq('metric_type', 'page_clicks')?.gte('metric_date', dateFilter)?.order('metric_date', { ascending: false });

      if (error) throw error;

      // Process page data for charts
      const pageData = this.processPageClickData(data);
      
      return { data: pageData, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to fetch page click metrics' };
    }
  },

  // Get session analytics
  async getSessionMetrics(artistId, timeRange = '30_days') {
    try {
      const dateFilter = this.getDateFilter(timeRange);
      
      const { data, error } = await supabase?.from('mobile_app_analytics')?.select('*')?.eq('artist_id', artistId)?.in('metric_type', ['session_duration', 'active_users'])?.gte('metric_date', dateFilter)?.order('metric_date', { ascending: false });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message || 'Failed to fetch session metrics' };
    }
  },

  // Helper: Get date filter based on time range
  getDateFilter(timeRange) {
    const now = new Date();
    switch (timeRange) {
      case '7_days':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      case '30_days':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      case '90_days':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      case 'all_time':
        return '2020-01-01';
      default:
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
    }
  },

  // Helper: Process metrics data for dashboard
  processMetricsData(mobileData, userAnalytics, activityData, timeRange) {
    const processed = {
      totalDownloads: 0,
      totalActiveUsers: 0,
      totalSessions: 0,
      averageSessionDuration: 0,
      chartData: [],
      geoData: {},
      pageClicks: {},
      trends: {}
    };

    // Process mobile analytics
    mobileData?.forEach(metric => {
      switch (metric?.metric_type) {
        case 'app_downloads':
          processed.totalDownloads += metric?.metric_value || 0;
          if (metric?.geographic_data) {
            processed.geoData = { ...processed?.geoData, ...metric?.geographic_data };
          }
          break;
        case 'active_users':
          processed.totalActiveUsers += metric?.metric_value || 0;
          break;
        case 'page_clicks':
          if (metric?.page_data) {
            const pageData = metric?.page_data;
            if (pageData?.page_name) {
              // Single page_name format from mobile app
              const page = pageData?.page_name;
              processed.pageClicks[page] = (processed?.pageClicks?.[page] || 0) + (metric?.metric_value || 1);
            } else {
              // Legacy aggregated format
              Object.entries(pageData)?.forEach(([page, clicks]) => {
                if (typeof clicks === 'number') {
                  processed.pageClicks[page] = (processed?.pageClicks?.[page] || 0) + clicks;
                }
              });
            }
          }
          break;
        case 'session_duration':
          processed.averageSessionDuration = metric?.metadata?.average_minutes || 0;
          break;
      }
    });

    // Process chart data for trends
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date?.setDate(date?.getDate() - i);
      return date?.toISOString()?.split('T')?.[0];
    })?.reverse();

    processed.chartData = last7Days?.map(date => {
      const dayData = mobileData?.filter(metric => 
        metric?.metric_date === date
      );
      
      return {
        date,
        downloads: dayData?.find(d => d?.metric_type === 'app_downloads')?.metric_value || 0,
        activeUsers: dayData?.find(d => d?.metric_type === 'active_users')?.metric_value || 0,
        pageClicks: dayData?.find(d => d?.metric_type === 'page_clicks')?.metric_value || 0
      };
    });

    return processed;
  },

  // Helper: Process geographic data
  processGeographicData(data) {
    const combined = {};
    
    data?.forEach(metric => {
      if (metric?.geographic_data) {
        Object.entries(metric?.geographic_data)?.forEach(([country, count]) => {
          combined[country] = (combined?.[country] || 0) + count;
        });
      }
    });

    // Convert to array format for mapping
    return Object.entries(combined)?.map(([country, count]) => ({
      country,
      count,
      percentage: 0 // Will be calculated in component
    }));
  },

  // Helper: Process page click data
  processPageClickData(data) {
    const combined = {};
    
    data?.forEach(metric => {
      if (metric?.page_data) {
        // page_data can be { page_name: 'home' } (single page) or { home: 5, about: 3 } (aggregated)
        const pageData = metric?.page_data;
        if (pageData?.page_name) {
          // Single page_name format from mobile app analytics
          const page = pageData?.page_name;
          combined[page] = (combined?.[page] || 0) + (metric?.metric_value || 1);
        } else {
          // Legacy key-value aggregated format
          Object.entries(pageData)?.forEach(([page, clicks]) => {
            if (typeof clicks === 'number') {
              combined[page] = (combined?.[page] || 0) + clicks;
            }
          });
        }
      }
    });

    // Convert to array format for charts
    return Object.entries(combined)?.map(([page, clicks]) => ({ page, clicks }))?.sort((a, b) => b?.clicks - a?.clicks);
  },

  // Real-time subscription for metrics updates
  subscribeToMetrics(artistId, callback) {
    return supabase?.channel(`metrics-${artistId}`)?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'mobile_app_analytics',
          filter: `artist_id=eq.${artistId}`
        },
        callback
      )?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'user_analytics',
          filter: `user_id=eq.${artistId}`
        },
        callback
      )?.subscribe();
  }
};

export default metricsService;